import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { ArrowRight, Heart, Sparkles, Users, BookOpen, Check, HeartHandshake, RefreshCw, Compass, Star, Quote } from "lucide-react";
import { toast } from "sonner";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    api.get("/testimonials/public").then(({ data }) => setTestimonials(data || [])).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7 animate-fade-in-up">
            <p className="text-xs tracking-[0.3em] uppercase text-[#C86B5E] font-semibold mb-6" data-testid="landing-tagline">
              Le parcours d'avant-mariage
            </p>
            <h1 className="font-heading font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-[#2C2A29]">
              Préparez votre <span className="italic text-[#C86B5E]">oui</span> à deux,
              <br />
              guidés par la sagesse, la psychologie
              <br />
              et la <span className="italic">Parole</span>.
            </h1>
            <p className="mt-8 text-lg text-[#5C5754] max-w-xl leading-relaxed">
              Un voyage de réflexion à deux, module par module. Chacun répond en privé,
              puis l'analyse vous est révélée — fondée sur la psychologie du couple,
              le conseil matrimonial et la théologie évangélique.
            </p>
            <p className="mt-4 text-base text-[#7A8B76] max-w-xl leading-relaxed font-medium">
              Fiancés, déjà mariés ou simplement en réflexion : ce parcours s'adapte à votre situation.
            </p>
            <div className="mt-12 flex flex-wrap gap-4">
              {user ? (
                <button
                  data-testid="cta-dashboard"
                  onClick={() => nav("/dashboard")}
                  className="bg-[#C86B5E] text-white hover:bg-[#B35A4E] rounded-full px-8 py-4 transition-colors flex items-center gap-3 text-base"
                >
                  Mon parcours <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <>
                  <button
                    data-testid="cta-start"
                    onClick={() => nav("/auth?mode=register")}
                    className="bg-[#C86B5E] text-white hover:bg-[#B35A4E] rounded-full px-8 py-4 transition-colors flex items-center gap-3 text-base"
                  >
                    Commencer notre parcours <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="cta-login"
                    onClick={() => nav("/auth?mode=login")}
                    className="bg-transparent text-[#2C2A29] border border-[#E6E2DD] hover:border-[#C86B5E] rounded-full px-8 py-4 transition-all"
                  >
                    Se connecter
                  </button>
                </>
              )}
            </div>
            <p className="mt-6 flex items-center gap-2 text-sm text-[#8A8480]" data-testid="hero-trust">
              <Heart className="w-4 h-4 text-[#C86B5E]" strokeWidth={1.5} />
              Fondé sur la psychologie du couple, le conseil matrimonial et la sagesse biblique.
            </p>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/5] shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1640273296013-e4b54eaf52eb?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Couple au coucher du soleil"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <p className="font-heading italic text-2xl leading-snug">
                  « Heureux sont ceux qui se préparent ensemble pour ce qui durera toute une vie. »
                </p>
              </div>
            </div>
            <div className="absolute -bottom-8 -left-8 bg-white border border-[#E6E2DD] rounded-2xl p-5 shadow-xl max-w-[200px] hidden lg:block">
              <p className="font-heading text-3xl text-[#C86B5E]">9</p>
              <p className="text-xs tracking-wider uppercase text-[#8A8480] mt-1">
                modules guidés
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 border-t border-[#E6E2DD]">
        <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4 text-center">
          Comment ça fonctionne
        </p>
        <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-center max-w-3xl mx-auto leading-tight">
          Un cheminement <span className="italic">simple</span>, profond et intime.
        </h2>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-16">
          {[
            {
              icon: Users,
              n: "01",
              title: "Vous vous connectez à deux",
              text: "Un partenaire crée le couple. L'autre rejoint avec un code d'invitation. Vos réponses restent privées.",
            },
            {
              icon: BookOpen,
              n: "02",
              title: "Vous répondez module par module",
              text: "9 thématiques essentielles : communication, finances, intimité, foi, conflits, vision... Chacun répond séparément, à son rythme.",
            },
            {
              icon: Sparkles,
              n: "03",
              title: "L'analyse vous est délivrée",
              text: "Quand les deux ont répondu, recevez une analyse profonde : synthèse, score de compatibilité, conseils psychologiques et bibliques, plan d'action.",
            },
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`step-card-${i}`}
              className="bg-white border border-[#E6E2DD] rounded-2xl p-8 hover:shadow-lg transition-shadow"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-12 h-12 rounded-full bg-[#F3EFEB] flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-[#C86B5E]" strokeWidth={1.5} />
                </div>
                <span className="font-heading text-2xl text-[#E6DCD3]">{s.n}</span>
              </div>
              <h3 className="font-heading text-2xl leading-snug mb-3">{s.title}</h3>
              <p className="text-[#5C5754] leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Audience */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 border-t border-[#E6E2DD]">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
            Pour qui ?
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight">
            Un parcours pour <span className="italic">chaque</span> couple.
          </h2>
          <p className="mt-6 text-[#5C5754] leading-relaxed">
            Que vous prépariez votre mariage ou que vous soyez déjà unis depuis des années,
            l'analyse s'ajuste à votre réalité — jamais l'inverse.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {[
            {
              icon: HeartHandshake,
              title: "Fiancés",
              text: "Vous préparez votre mariage. Posez des fondations solides et abordez les sujets essentiels avant le grand jour.",
            },
            {
              icon: RefreshCw,
              title: "Déjà mariés ? Faites le point ensemble",
              text: "Mariés depuis quelques mois ou plusieurs années, sans jamais avoir suivi de préparation ? Faites un bilan et renforcez votre union.",
              featured: true,
            },
            {
              icon: Compass,
              title: "En réflexion",
              text: "En couple et vous vous interrogez sur le mariage ? Discernez ensemble, à votre rythme, ce qui vous attend.",
            },
          ].map((a, i) => (
            <div
              key={i}
              data-testid={`audience-card-${i}`}
              className={`rounded-2xl p-8 border transition-all ${
                a.featured
                  ? "bg-[#2C2A29] text-white border-[#2C2A29] shadow-2xl md:-translate-y-3"
                  : "bg-white border-[#E6E2DD] hover:shadow-lg"
              }`}
            >
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-6 ${a.featured ? "bg-white/10" : "bg-[#F3EFEB]"}`}>
                <a.icon className={`w-5 h-5 ${a.featured ? "text-[#D4A373]" : "text-[#C86B5E]"}`} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-2xl leading-snug mb-3">{a.title}</h3>
              <p className={`leading-relaxed ${a.featured ? "text-white/80" : "text-[#5C5754]"}`}>{a.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Modules teaser */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 border-t border-[#E6E2DD]">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
              Les modules
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight">
              9 thèmes pour bâtir — ou renforcer — un mariage <span className="italic">solide</span>.
            </h2>
          </div>
          <div className="lg:col-span-5 text-[#5C5754] leading-relaxed">
            Chaque module aborde un pilier du mariage à travers des questions intimes,
            puis livre une analyse personnalisée nourrie par les meilleurs outils du conseil
            matrimonial et la sagesse biblique.
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {[
            "Fondations & Histoire",
            "Pourquoi nous marier",
            "Communication",
            "Gestion des conflits",
            "Famille & Enfants",
            "Spiritualité partagée",
            "Intimité & Sexualité",
            "Finances & Gérance",
            "Vision & Mission",
          ].map((t, i) => (
            <div
              key={i}
              className="bg-[#F3EFEB] border border-[#E6E2DD] rounded-xl px-5 py-6 hover:bg-white hover:border-[#C86B5E]/40 transition-all"
            >
              <Heart className="w-4 h-4 text-[#C86B5E] mb-3" strokeWidth={1.5} />
              <p className="font-heading text-lg leading-tight">{t}</p>
            </div>
          ))}
        </div>
        <div className="mt-16 text-center">
          {!user && (
            <button
              data-testid="cta-bottom"
              onClick={() => nav("/auth?mode=register")}
              className="bg-[#C86B5E] text-white hover:bg-[#B35A4E] rounded-full px-10 py-4 transition-colors inline-flex items-center gap-3"
            >
              Commencer maintenant <ArrowRight className="w-4 h-4" />
            </button>
          )}
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 border-t border-[#E6E2DD]" data-testid="testimonials-section">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
              Ils l'ont vécu
            </p>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight">
              Des couples et des cœurs <span className="italic">transformés</span>.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div
                key={t.id}
                data-testid={`testimonial-card-${t.id}`}
                className={`rounded-2xl p-7 border transition-all ${
                  t.featured
                    ? "bg-[#2C2A29] text-white border-[#2C2A29] shadow-xl"
                    : "bg-white border-[#E6E2DD]"
                }`}
              >
                <Quote className={`w-6 h-6 mb-4 ${t.featured ? "text-[#D4A373]" : "text-[#C86B5E]"}`} strokeWidth={1.5} />
                <div className="flex items-center gap-0.5 mb-4">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className="w-4 h-4"
                      strokeWidth={1.5}
                      style={{
                        fill: t.rating >= n ? "#D4A373" : "transparent",
                        color: t.rating >= n ? "#D4A373" : t.featured ? "#5b5854" : "#C9C2BA",
                      }}
                    />
                  ))}
                </div>
                <p className={`leading-relaxed mb-5 ${t.featured ? "text-white/90" : "text-[#2C2A29]"}`}>
                  « {t.content} »
                </p>
                <p className={`text-sm font-medium ${t.featured ? "text-[#D4A373]" : "text-[#C86B5E]"}`}>
                  {t.display_name}
                  <span className={`font-normal ${t.featured ? "text-white/50" : "text-[#8A8480]"}`}> · {t.status_label}</span>
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Pricing */}
      <section id="tarifs" className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 border-t border-[#E6E2DD]">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs tracking-[0.3em] uppercase text-[#7A8B76] font-semibold mb-4">
            Tarifs
          </p>
          <h2 className="font-heading text-4xl sm:text-5xl tracking-tight leading-tight">
            Commencez <span className="italic">gratuitement</span>, débloquez à votre rythme.
          </h2>
          <p className="mt-6 text-[#5C5754] leading-relaxed">
            Répondez et comparez vos réponses gratuitement. Les analyses approfondies se
            débloquent module par module, ou en une fois avec le pack complet.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 text-sm text-[#7A8B76] font-medium" data-testid="pricing-trust">
            <Heart className="w-4 h-4" strokeWidth={1.5} />
            Chaque analyse s'appuie sur la psychologie du couple et la sagesse biblique.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 items-stretch">
          {/* Free */}
          <div data-testid="pricing-free" className="bg-white border border-[#E6E2DD] rounded-3xl p-8 flex flex-col">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] font-semibold">Découverte</p>
            <p className="font-heading text-5xl mt-4 text-[#2C2A29]">Gratuit</p>
            <p className="text-sm text-[#8A8480] mt-2">Pour démarrer à deux</p>
            <ul className="mt-8 space-y-3 flex-1">
              {["Les 9 modules à remplir", "Analyse du module « Communication » offerte", "Vue comparée de vos réponses", "Suivi de progression"].map((f) => (
                <li key={f} className="flex gap-3 text-[#5C5754]">
                  <Check className="w-5 h-5 text-[#7A8B76] flex-shrink-0" strokeWidth={2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-free-cta"
              onClick={() => nav(user ? "/dashboard" : "/auth?mode=register")}
              className="mt-8 w-full border border-[#C86B5E] text-[#C86B5E] hover:bg-[#C86B5E] hover:text-white rounded-full px-6 py-3.5 transition-colors"
            >
              Commencer gratuitement
            </button>
          </div>

          {/* Pack (popular) */}
          <div data-testid="pricing-pack" className="bg-[#2C2A29] text-white rounded-3xl p-8 flex flex-col relative shadow-2xl md:-translate-y-3">
            <span className="absolute top-6 right-6 text-[10px] tracking-[0.2em] uppercase bg-[#D4A373] text-[#2C2A29] font-semibold rounded-full px-3 py-1">
              Le plus choisi
            </span>
            <p className="text-xs tracking-[0.2em] uppercase text-[#D4A373] font-semibold">Pack complet</p>
            <p className="font-heading text-5xl mt-4">29€</p>
            <p className="text-sm text-white/60 mt-2">Les 9 analyses · au lieu de 54€</p>
            <ul className="mt-8 space-y-3 flex-1">
              {["Analyse approfondie des 9 modules", "Score d'alignement par module", "Conseils psychologiques & bibliques", "Plan d'action personnalisé", "Export PDF de chaque analyse"].map((f) => (
                <li key={f} className="flex gap-3 text-white/90">
                  <Check className="w-5 h-5 text-[#D4A373] flex-shrink-0" strokeWidth={2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-pack-cta"
              onClick={() => nav(user ? "/dashboard" : "/auth?mode=register")}
              className="mt-8 w-full bg-[#C86B5E] hover:bg-[#B35A4E] text-white rounded-full px-6 py-3.5 transition-colors inline-flex items-center justify-center gap-2"
            >
              Choisir le pack <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Per module */}
          <div data-testid="pricing-module" className="bg-white border border-[#E6E2DD] rounded-3xl p-8 flex flex-col">
            <p className="text-xs tracking-[0.2em] uppercase text-[#8A8480] font-semibold">À la carte</p>
            <p className="font-heading text-5xl mt-4 text-[#2C2A29]">5,99€<span className="text-lg text-[#8A8480]"> /module</span></p>
            <p className="text-sm text-[#8A8480] mt-2">Débloquez ce qui compte</p>
            <ul className="mt-8 space-y-3 flex-1">
              {["Analyse d'un module au choix", "Score, forces & points à explorer", "Conseils et plan d'action", "Export PDF de l'analyse"].map((f) => (
                <li key={f} className="flex gap-3 text-[#5C5754]">
                  <Check className="w-5 h-5 text-[#7A8B76] flex-shrink-0" strokeWidth={2} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid="pricing-module-cta"
              onClick={() => nav(user ? "/dashboard" : "/auth?mode=register")}
              className="mt-8 w-full border border-[#C86B5E] text-[#C86B5E] hover:bg-[#C86B5E] hover:text-white rounded-full px-6 py-3.5 transition-colors"
            >
              Débloquer un module
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
